import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Create, GetAllEmployees } from "adminApi/HousekeepingApi/Housekeeping.js";
import { validateForm } from "utils/validation";
import { HousekeepingRules } from "validation/ValidationRules";
import Spinner from "Common/Globle/Spinner";
import fields from "../json/HousekeepingFields.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import useFetchAllData from "Hooks/useFetchAllData";
import AddHousekeepingForm from "./components/AddHousekeepingForm";

/**
 * AddHousekeeping — smart container
 * Owns all state, API calls, and side effects.
 * Passes data + callbacks down to AddHousekeepingForm (dumb presentational).
 */
const AddHousekeeping = () => {
    const navigate = useNavigate();
    const { formdata, searchdata } = fields;

    // Build initial form state from all field definitions
    const allFields = useMemo(
        () => [...formdata, ...searchdata, { name: "limit" }],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const initialValues = useMemo(
        () => allFields.reduce((acc, field) => ({ ...acc, [field.name]: "" }), {}),
        [allFields]
    );

    const { values, errors, handleChange, setErrors, setValues } = useFormChange(
        initialValues,
        HousekeepingRules
    );

    // Managers and Cleaners — pre-filtered at DB level
    const { data: employeeData } = useFetchAllData(GetAllEmployees);
    const managers = employeeData?.managers ?? [];
    const cleaners = employeeData?.cleaners ?? [];

    // Submit handler
    const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
        async (formValues) => {
            const formData = new FormData();

            // room_ids is stored as an array in state — append each as room_ids[]
            // so express-urlencoded parses it back into an array on the backend
            const roomIdsArray = Array.isArray(formValues.room_ids)
                ? formValues.room_ids
                : typeof formValues.room_ids === "string"
                    ? formValues.room_ids.split(",").map((v) => v.trim()).filter(Boolean)
                    : [];

            roomIdsArray.forEach((id) => formData.append("room_ids[]", id));

            // Append all other fields (skip room_ids — already handled above)
            const { room_ids: _skip, ...rest } = formValues;
            Object.entries(rest).forEach(([key, val]) => formData.append(key, val ?? ""));

            return await Create({ data: formData });
        },
        (vals) => {
            const err = validateForm(vals, HousekeepingRules);
            setErrors(err);
            return err;
        },
        () => navigate(-1),
        "Housekeeping task created successfully."
    );

    // Only submit the fields the backend needs
    const submitPayload = {
        assigned_by: values.assigned_by,
        assigned_to: values.assigned_to,
        start_time: values.start_time,
        end_time: values.end_time,
        room_ids: values.room_ids,
        inventory_issued: values.inventory_issued,
    };

    return (
        <>
            {loadingSubmit && <Spinner isSpinner />}
            <AddHousekeepingForm
                fields={formdata}
                values={values}
                errors={errors}
                handleChange={handleChange}
                handleSubmit={(e) => handleSubmit(e, submitPayload)}
                loading={loadingSubmit}
                managers={managers}
                cleaners={cleaners}
                setValues={setValues}
            />
        </>
    );
};

export default AddHousekeeping;
